import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  Input,
} from '@angular/core';
import { MicrofrontendComponent } from '../microfrontend.component';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../shared/services/auth/auth.service';
import { Params, Router } from '@angular/router';
import { CustomerService } from '../../shared/services';
import {
  CompanyModuleService,
  CompanyService,
} from '../../shared/services/company';
import {
  AnalyticsService,
  Source,
} from '../../shared/services/analytics/analytics.service';
import * as Sentry from '@sentry/angular-ivy';
import { authRoutes } from '../../core/auth/auth-routing.module';
import CampaignManager from '../../shared/services/campaign/campaignManager.service';
import { NativeAuthService } from './native-auth.service';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '../../shared/services/toast/toast.service';
import { I18nService } from '@web-frontend/shared/i18n';
import { ModulesService } from '@web-frontend/shared/services/modules/modules.service';
import { FeatureTypes } from '@web-frontend/shared/enums/module';
import { InstallationStateService } from '@web-frontend/shared/services/installation-state/installation-state.service';
import { PlatformService } from '@web-frontend/shared/components/platform-disable-content/platform.service';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export enum EventType {
  loginSuccessfully = 'loginSuccessfully',
  navigateToOnboarding = 'navigateToOnboarding',
  navigateToApp = 'navigateToApp',
  navigateToRecoverPassword = 'navigateToRecoverPassword',
  sendAnalyticsEvent = 'sendAnalyticsEvent',
  nativeLogin = 'nativeLogin',
  nativeLoginResult = 'auth:nativeLoginResult',
  nativeLoginError = 'auth:nativeLoginError',
  registrationComplete = 'registrationComplete',
  navigateToLogin = 'navigateToLogin',
  finishNoLoginRegister = 'finishNoLoginRegister',
  showToast = 'showToast',
  navigate = 'navigate',
  switchCompany = 'switchCompany',
  createCompany = 'createCompany',
}

type EventPayload =
  | { resource: string }
  | { event: string }
  | { path: string }
  | { companyId: string };

type SendEventPayload = {
  success?: boolean;
  token?: string;
  email?: string;
  error?: string;
};

type EventData = {
  type: EventType;
  payload?: EventPayload;
};

type SendEventData = {
  type: EventType;
  payload?: SendEventPayload;
};

@Component({
  selector: 'roma-mf-auth',
  template: `<mf-auth [attr.isnologin]="isNoLogin"></mf-auth>`,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MfAuthComponent extends MicrofrontendComponent<
  EventData,
  SendEventData
> {
  @Input() appName = 'mf-auth';
  @Input() appUrl: string = localStorage.getItem('mf-auth');
  @Input() isNoLogin = false;

  constructor(
    private _httpClient: HttpClient,
    private _el: ElementRef,
    private readonly authService: AuthService,
    router: Router,
    private readonly customerService: CustomerService,
    private readonly companyModuleService: CompanyModuleService,
    private readonly modulesService: ModulesService,
    private readonly dialog: MatDialog,
    private readonly analyticsService: AnalyticsService,
    private readonly campaignManager: CampaignManager,
    private readonly toastService: ToastService,
    private i18n: I18nService,
    private injector: Injector,
    private readonly platformService: PlatformService,
    private companyService: CompanyService
  ) {
    super(_httpClient, _el, router);
    this.exposeNativeAuth();
    this.handleCustomEvent = this.handleCustomEvent.bind(this);
    this.handleNavigateToLogin = this.handleNavigateToLogin.bind(this);
    this.handleFinishNoLoginRegister = this.handleFinishNoLoginRegister.bind(
      this
    );
  }

  onInit(): void {
    this.campaignManager.fetchCampaign();
    this.i18n.initializeTranslate();
  }

  private async initLegacyFlow(): Promise<void> {
    await this.authService.loginSuccessfullyFlow();
    this.companyModuleService.init();
    this.customerService.loginSuccessfullyFlow();

    // Mark user as having logged in for mobile app scenarios only
    if (this.platformService.isNative()) {
      const installationStateService = this.injector.get(
        InstallationStateService
      );
      installationStateService.markUserHasLoggedIn();
    }
  }

  private handleAnalyticsEvent({
    event,
    sources,
    properties = {},
  }: {
    event: string;
    sources: Source[];
    properties?: { [key: string]: string };
  }): void {
    this.analyticsService.trackEvent({
      sources: sources,
      eventName: event,
      eventProperties: properties,
    });
  }

  private async handleEventRegisterSave(): Promise<void> {
    await this.dispatchRegistrationFormSubmit();
    this.analyticsService.identifyUser();

    // Mark user as having registered for mobile app scenarios only
    if (this.platformService.isNative()) {
      const installationStateService = this.injector.get(
        InstallationStateService
      );
      installationStateService.markUserHasRegistered();
    }
  }

  private dispatchRegistrationFormSubmit(): Promise<void> {
    return new Promise((resolve) => {
      try {
        window.dataLayer.push({
          event: 'GTM_form_submit',
          registrationStatus: 'success',
        });
      } catch (error) {
        console.error('Error dispatching registration form submit:', error);
        Sentry.captureException(error);
      }
      resolve();
    });
  }

  private handleNavigateToLogin(): void {
    this.authService.logout();
    this.router.navigate([authRoutes.login]);
  }

  handleCustomEvent(event: CustomEvent): void {
    const eventMap = {
      [EventType.navigateToApp]: () => this.handleNavigateToApp(),
      [EventType.navigateToRecoverPassword]: () =>
        this.router.navigate([authRoutes.recover]),
      [EventType.sendAnalyticsEvent]: () =>
        this.handleAnalyticsEvent(event.detail.payload),
      [EventType.registrationComplete]: () =>
        this.analyticsService.identifyUser(),
      [EventType.navigateToLogin]: this.handleNavigateToLogin,
      [EventType.finishNoLoginRegister]: this.handleFinishNoLoginRegister,
      [EventType.showToast]: () => this.handleShowToast(event.detail.payload),
      [EventType.navigate]: () => this.handleNavigate(event.detail.payload),
      [EventType.navigateToOnboarding]: async () => {
        await this.handleRegisterSuccessfully({
          url: event.detail.payload?.url,
          params: event.detail.payload?.params,
        });
      },
      [EventType.createCompany]: () => this.handleAddCompany(),
      [EventType.switchCompany]: () =>
        this.handleSwitchCompany(event.detail.payload).catch(() => {
          this.publish({ type: EventType.switchCompany });
        }),
    };
    eventMap[event.detail.type]?.();
  }

  handleAddCompany(): void {
    this.companyService
      .addCompany()
      .then((id) => this.handleSwitchCompany({ companyId: id }))
      .catch(() => {
        this.publish({ type: EventType.createCompany });
      });
  }

  handleNavigateToApp = async () => {
    await this.initLegacyFlow();
    this.companyModuleService.goFirstRouteActive();
  };

  async handleSwitchCompany({ companyId }): Promise<void> {
    await this.authService.switchCompany(companyId).then(() => {
      this.handleNavigateToApp();
    });
  }

  onDestroy(): void {}

  async handleNavigate({
    path,
    params,
  }: {
    path: string;
    params?: Params;
  }): Promise<void> {
    this.router.navigate([path], { queryParams: params });
  }

  private exposeNativeAuth(): void {
    const authService = this.injector.get(NativeAuthService);
    // Attach service methods to window for simplicity
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).NativeAuthService = {
      loginWithGoogle: authService.loginWithGoogle.bind(authService),
      loginWithApple: authService.loginWithApple.bind(authService),
    };
  }

  private async handleFinishNoLoginRegister(): Promise<void> {
    const openDialogs = this.dialog.openDialogs;
    await this.authService.refreshCacheUserRequest().toPromise();
    this.handleEventRegisterSave();
    const events = this.campaignManager.getCampaign()?.events as string[];
    for (const event of events) {
      this.analyticsService.trackEvent({
        eventName: event,
        sources: ['amplitude'],
        eventProperties: { noLogin: true },
      });
    }

    openDialogs?.forEach((dialogRef) => {
      if (dialogRef.id.startsWith('no-login-register-modal')) {
        dialogRef.close(true);
      }
    });
  }

  handleShowToast({
    type,
    text,
  }: {
    type: 'info' | 'success' | 'error';
    text: string;
  }): void {
    this.toastService.show({
      text,
      type,
      msDuration: 4000,
    });
  }

  async onRegisterNavigate(urlToNavigate: string): Promise<void> {
    const maxAttempts = 5;
    let attempt = 0;

    const tryCheck = async () => {
      attempt++;
      try {
        const response = await this.modulesService
          .getCompanyModules()
          .toPromise();

        const finance = response?.find((mod) => mod.name === 'finance');

        if (
          finance?.features?.find((f) => f.name === FeatureTypes.INVOICES)
            ?.enabled
        ) {
          await this.router.navigate([urlToNavigate]);
          return;
        }
      } catch (e) {
        console.warn(e);
      }

      if (attempt < maxAttempts) {
        setTimeout(tryCheck, 1000);
      } else {
        this.router.navigate(['admin/module-management']);
      }
    };

    tryCheck();
  }

  public async handleRegisterSuccessfully({
    url,
    params,
  }: {
    url: string;
    params: Record<string, string> | undefined;
  }): Promise<void> {
    await this.handleEventRegisterSave();
    await this.initLegacyFlow();
    if (url) {
      await this.onRegisterNavigate(url);
    } else {
      this.router.navigate(['admin/module-management']);
    }
  }
}
